import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert, Snackbar, CircularProgress } from '@mui/material';
import { tokens } from '../theme';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from './AppLayout';
import DeviceList from './DeviceList';
import StatsCards from './StatsCards';
import AdvancedStats from './AdvancedStats';
import EnhancedDeviceTable from './EnhancedDeviceTable';
import SaaSManagement from './SaaSManagement';
import RemoteView from './RemoteView';
import CommandDialog from './shared/CommandDialog';
import ConfirmDialog from './shared/ConfirmDialog';
import { deviceService, adminService } from '../services/api';

const Dashboard = () => {
  const { user } = useAuth();

  const [currentView, setCurrentView] = useState('dashboard');
  const [devices, setDevices]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [permissions, setPermissions]         = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Feedback
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  // Dialogs
  const [cmdDialog, setCmdDialog] = useState({
    open: false, type: null, devices: [],
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: '', message: '', onConfirm: null, severity: 'warning',
  });

  const can = useCallback((permission) => permissions.includes(permission), [permissions]);

  const showSnack = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  useEffect(() => {
    loadDevices();
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      const res = await adminService.getMyPermissions();
      if (res.success) setPermissions(res.data.permissions || []);
    } catch (e) {
      console.error('Error cargando permisos:', e);
    } finally {
      setPermissionsLoaded(true);
    }
  };

  const loadDevices = async () => {
    try {
      setLoading(true);
      const res = await deviceService.getDevices();
      if (res.success) {
        setDevices(
          res.devices.map((d) => ({
            ...d,
            device_name: d.device_name || d.model || 'Sin asignar',
            location: d.location || 'Sin ubicación',
          }))
        );
      } else {
        showSnack('No se pudieron cargar los dispositivos', 'error');
      }
    } catch {
      showSnack('Error de conexión con el servidor', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Acciones de dispositivo ─────────────────────────────────────────────
  const PERMISSION_MAP = {
    suspend:        'devices.suspend',
    activate:       'devices.activate',
    message:        'devices.send_message',
    wallpaper:      'devices.change_wallpaper',
    reboot:         'devices.suspend',
    update_app:     'devices.suspend',
    set_language:   'devices.manage_access_icons',
    home_shortcut:  'devices.manage_access_icons',
  };

  const handleDeviceAction = (action, selectedDevices = []) => {
    if (!selectedDevices.length) return;

    const requiredPermission = PERMISSION_MAP[action];
    if (requiredPermission && !can(requiredPermission)) {
      showSnack('Tu rol no tiene permiso para esta acción', 'error');
      return;
    }

    // Acciones que requieren confirmación sin formulario adicional
    if (action === 'reboot') {
      setConfirmDialog({
        open: true,
        title: 'Reiniciar dispositivos',
        message: `¿Reiniciar ${selectedDevices.length} dispositivo${selectedDevices.length !== 1 ? 's' : ''}? El proceso tarda entre 30 y 60 segundos.`,
        severity: 'warning',
        onConfirm: () => executeCommand('reboot', selectedDevices, {}),
      });
      return;
    }

    // Acciones de estado directo (sin formulario)
    if (action === 'suspend' || action === 'activate') {
      const isMany = selectedDevices.length > 1;
      const label = action === 'suspend' ? 'suspender' : 'activar';
      const deviceLabel = isMany
        ? `${selectedDevices.length} dispositivos`
        : (selectedDevices[0]?.device_name || 'este dispositivo');

      setConfirmDialog({
        open: true,
        title: action === 'suspend' ? 'Suspender dispositivos' : 'Activar dispositivos',
        message: `¿Confirmas ${label} ${deviceLabel}?`,
        severity: action === 'suspend' ? 'warning' : 'info',
        onConfirm: () => executeDirectAction(action, selectedDevices),
      });
      return;
    }

    // Acciones que necesitan formulario — abrir CommandDialog
    setCmdDialog({ open: true, type: action, devices: selectedDevices });
  };

  const executeDirectAction = async (action, selectedDevices) => {
    setConfirmDialog((d) => ({ ...d, open: false }));
    try {
      if (action === 'suspend') {
        await Promise.all(selectedDevices.map((d) => deviceService.suspendDevice(d.id)));
        showSnack(`${selectedDevices.length} dispositivo(s) suspendido(s)`);
      } else {
        await Promise.all(selectedDevices.map((d) => deviceService.activateDevice(d.id)));
        showSnack(`${selectedDevices.length} dispositivo(s) activado(s)`);
      }
      await loadDevices();
    } catch {
      showSnack('No se pudo completar la acción', 'error');
    }
  };

  const executeCommand = async (commandType, selectedDevices, payload) => {
    setConfirmDialog((d) => ({ ...d, open: false }));
    try {
      await Promise.all(
        selectedDevices.map((d) => deviceService.sendCommand(d.id, commandType, payload))
      );
      const labels = {
        message:      'Mensaje enviado',
        wallpaper:    'Cambio de fondo enviado',
        reboot:       'Comando de reinicio enviado',
        update_app:   'Actualización enviada',
        set_language: 'Cambio de idioma enviado',
        home_shortcut:'Accesos configurados',
      };
      showSnack(
        `${labels[commandType] || 'Comando enviado'} a ${selectedDevices.length} dispositivo(s)`
      );
      await loadDevices();
    } catch {
      showSnack('No se pudo enviar el comando', 'error');
      throw new Error('command failed'); // para que CommandDialog no se cierre
    }
  };

  // ─── Contenido por vista ─────────────────────────────────────────────────
  const renderContent = () => {
    switch (currentView) {
      case 'devices':
        return (
          <Box>
            {/* Tabla con filtros + selección múltiple */}
            <EnhancedDeviceTable
              devices={devices}
              loading={loading}
              onRefresh={loadDevices}
              onAction={handleDeviceAction}
            />
          </Box>
        );

      case 'stats':
        return <AdvancedStats devices={devices} />;

      case 'saas':
        return <SaaSManagement />;

      case 'remote':
        // Esperar a que los permisos carguen antes de bloquear el acceso
        if (!permissionsLoaded) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={28} thickness={3} sx={{ color: tokens.color.brand[500] }} />
            </Box>
          );
        }
        if (!can('devices.remote_support')) {
          return (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              Tu rol no tiene acceso al soporte remoto.
            </Box>
          );
        }
        return <RemoteView devices={devices} />;

      case 'dashboard':
      default:
        return (
          <Box>
            <StatsCards />
            <Box sx={{ mt: 3 }}>
              <AdvancedStats devices={devices} />
            </Box>
          </Box>
        );
    }
  };

  return (
    <AppLayout currentView={currentView} onNavigate={setCurrentView}>
      {renderContent()}

      {/* Command dialog — reemplaza todos los window.prompt() */}
      <CommandDialog
        open={cmdDialog.open}
        commandType={cmdDialog.type}
        devices={cmdDialog.devices}
        onClose={() => setCmdDialog({ open: false, type: null, devices: [] })}
        onExecute={executeCommand}
      />

      {/* Confirm dialog — reemplaza todos los window.confirm() */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        severity={confirmDialog.severity}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((d) => ({ ...d, open: false }))}
      />

      {/* Snackbar de feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ minWidth: 280 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </AppLayout>
  );
};

export default Dashboard;
